import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import {
  FaCalendarAlt,
  FaCompressAlt,
  FaEnvelope,
  FaExpandAlt,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegStar,
  FaStar,
  FaUserTie,
} from "react-icons/fa";

import {
  cx,
  formatDateFR,
  getClientCardLabelColor,
  getContactsCount,
  getLogoUrl,
  getMainContact,
  getRelanceBadgeState,
  isRelanceDue,
  stripHtml,
} from "../utils/suiviClientsUtils";

export default function SuiviClientsCard({
  association,
  index,
  expanded,
  onToggleExpanded,
  onOpenAssociation,
}) {
  const logoUrl = getLogoUrl(association.logo_asso);
  const mainContact = getMainContact(association);
  const contactsCount = getContactsCount(association);
  const relanceDue = isRelanceDue(association);
  const relanceState = getRelanceBadgeState(association);
  const detailPreview = stripHtml(association.detail_asso || "");
  const labelColor = getClientCardLabelColor(association);

  return (
    <Draggable draggableId={`association-${association.id}`} index={index}>
      {(provided, snapshot) => (
        <article
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={cx(
            "group relative overflow-hidden rounded-2xl border bg-white shadow-sm ring-1 ring-black/[0.03] transition hover:-translate-y-0.5 hover:shadow-xl",
            relanceDue
              ? "border-red-200 hover:border-red-300"
              : "border-slate-200 hover:border-yellow-300",
            snapshot.isDragging && "rotate-[1deg] shadow-2xl",
          )}
        >
          <div
            className="absolute inset-y-0 left-0 w-1.5"
            style={{ backgroundColor: labelColor }}
          />

          {!expanded ? (
            <CompactCard
              association={association}
              relanceDue={relanceDue}
              relanceState={relanceState}
              labelColor={labelColor}
              onOpenAssociation={onOpenAssociation}
              onToggleExpanded={onToggleExpanded}
            />
          ) : (
            <ExpandedCard
              association={association}
              logoUrl={logoUrl}
              mainContact={mainContact}
              contactsCount={contactsCount}
              relanceDue={relanceDue}
              relanceState={relanceState}
              detailPreview={detailPreview}
              labelColor={labelColor}
              onOpenAssociation={onOpenAssociation}
              onToggleExpanded={onToggleExpanded}
            />
          )}
        </article>
      )}
    </Draggable>
  );
}

function CompactCard({
  association,
  relanceDue,
  relanceState,
  labelColor,
  onOpenAssociation,
  onToggleExpanded,
}) {
  return (
    <div className="pl-2">
      <div className="p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span
            className="max-w-[175px] truncate rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-800"
            style={{ backgroundColor: `${labelColor}33` }}
          >
            {association.produit_asso || "Produit non défini"}
          </span>

          <span className="shrink-0 text-yellow-500">
            {association.is_favorite ? <FaStar /> : <FaRegStar />}
          </span>
        </div>

        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenAssociation?.(association);
            }}
            className="min-w-0 text-left"
          >
            <h3 className="line-clamp-2 text-sm font-bold leading-snug text-[#172b4d]">
              {association.nom_asso || "Association sans nom"}
            </h3>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpanded?.(association.id);
            }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-violet-100 hover:text-violet-700"
            title="Agrandir cette carte"
          >
            <FaExpandAlt className="text-xs" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span
            className={cx(
              "inline-flex min-w-0 items-center gap-1.5 rounded-xl px-2 py-1 text-[11px] font-bold",
              relanceDue
                ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-600",
            )}
            title="Date de relance / fin d’abonnement"
          >
            <FaCalendarAlt className="shrink-0" />
            <span className="truncate">
              {association.date_relance_asso
                ? formatDateFR(association.date_relance_asso)
                : "Sans date"}
            </span>
          </span>

          {association.date_relance_asso && (
            <span
              className={cx(
                "shrink-0 rounded-full px-2 py-1 text-[10px] font-bold",
                relanceState.tone === "danger" && "bg-red-600 text-white",
                relanceState.tone === "warning" &&
                  "bg-amber-100 text-amber-700",
                relanceState.tone === "safe" && "bg-emerald-100 text-emerald-700",
                relanceState.tone === "muted" && "bg-slate-100 text-slate-500",
              )}
            >
              {relanceState.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ExpandedCard({
  association,
  logoUrl,
  mainContact,
  contactsCount,
  relanceDue,
  relanceState,
  detailPreview,
  labelColor,
  onOpenAssociation,
  onToggleExpanded,
}) {
  return (
    <div className="pl-2">
      {logoUrl && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpenAssociation?.(association);
          }}
          className="block max-h-36 w-full overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100"
        >
          <img
            src={logoUrl}
            alt={association.nom_asso}
            loading="lazy"
            className="max-h-36 w-full object-contain p-3"
          />
        </button>
      )}

      <div className="p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span
              className="h-2 w-10 rounded-full"
              style={{ backgroundColor: labelColor }}
            />

            {association.produit_asso && (
              <span className="max-w-[170px] truncate rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                {association.produit_asso}
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenAssociation?.(association);
              }}
              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-blue-100 hover:text-blue-700"
              title="Ouvrir la fiche"
            >
              <FaExternalLinkAlt className="text-xs" />
            </button>

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpanded?.(association.id);
              }}
              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-violet-100 hover:text-violet-700"
              title="Réduire cette carte"
            >
              <FaCompressAlt className="text-xs" />
            </button>
          </div>
        </div>

        <div className="mb-2 flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenAssociation?.(association);
            }}
            className="min-w-0 text-left"
          >
            <h3 className="line-clamp-3 text-sm font-bold leading-snug text-[#172b4d]">
              {association.nom_asso || "Association sans nom"}
            </h3>
          </button>

          <span className="mt-0.5 shrink-0 text-yellow-500">
            {association.is_favorite ? <FaStar /> : <FaRegStar />}
          </span>
        </div>

        {detailPreview && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenAssociation?.(association);
            }}
            className="mb-2 line-clamp-3 rounded-xl bg-slate-50 px-3 py-2 text-left text-xs leading-relaxed text-slate-500"
          >
            {detailPreview}
          </button>
        )}

        {mainContact && (
          <div className="mb-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div className="flex items-center gap-1.5 font-bold text-slate-700">
              <FaUserTie className="text-slate-400" />
              <span className="truncate">
                {mainContact.prenom} {mainContact.nom}
              </span>
            </div>

            {mainContact.fonction && (
              <div className="mt-0.5 truncate pl-5 text-slate-500">
                {mainContact.fonction}
              </div>
            )}
          </div>
        )}

        <div className="mb-2 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
          {association.discipline_asso && (
            <span className="rounded-lg bg-slate-100 px-2 py-1 font-bold">
              {association.discipline_asso}
            </span>
          )}

          {association.statut_asso && (
            <span className="rounded-lg bg-slate-100 px-2 py-1 font-bold">
              {association.statut_asso}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span
            className={cx(
              "inline-flex items-center gap-1 rounded-xl px-2 py-1 font-bold",
              relanceDue
                ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-600",
            )}
          >
            <FaCalendarAlt />
            {association.date_relance_asso
              ? formatDateFR(association.date_relance_asso)
              : "Sans relance"}
          </span>

          {association.date_relance_asso && (
            <span
              className={cx(
                "inline-flex items-center rounded-xl px-2 py-1 text-[11px] font-bold",
                relanceState.tone === "danger" && "bg-red-600 text-white",
                relanceState.tone === "warning" &&
                  "bg-amber-100 text-amber-700",
                relanceState.tone === "safe" && "bg-emerald-100 text-emerald-700",
                relanceState.tone === "muted" && "bg-slate-100 text-slate-500",
              )}
            >
              {relanceState.label}
            </span>
          )}

          {contactsCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <FaUserTie />
              {contactsCount}
            </span>
          )}

          {association.mail_asso && (
            <span className="inline-flex items-center gap-1">
              <FaEnvelope />
            </span>
          )}

          {association.tel_asso && (
            <span className="inline-flex items-center gap-1">
              <FaPhoneAlt />
            </span>
          )}

          {(association.latitude || association.longitude) && (
            <span className="inline-flex items-center gap-1">
              <FaMapMarkerAlt />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}